/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1784505600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sinch-messagemedia",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Sinch MessageMedia",
          "depth": 0,
          "id": "connect-to-sinch-messagemedia"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sync contacts",
          "depth": 1,
          "id": "sync-contacts"
        }, {
          "value": "Sync contact lists",
          "depth": 1,
          "id": "sync-contact-lists"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Recommended workflow",
          "depth": 1,
          "id": "recommended-workflow"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ol: "ol",
      li: "li",
      ul: "ul",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#sync-contacts",
                children: _jsx(_components.strong, {
                  children: "Contacts"
                })
              })
            }), _jsx(_components.td, {
              children: "Upsert contact records (phone numbers and attributes) into Sinch's contact directory."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.messagemedia.com/",
                children: "Contacts API"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#sync-contact-lists",
                children: _jsx(_components.strong, {
                  children: "Contact lists"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync Hightouch audiences into long-lived Sinch contact lists."
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.messagemedia.com/",
                children: "Contact lists API"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sinch-messagemedia",
        children: "Connect to Sinch MessageMedia"
      }), "\n", _jsx(_components.p, {
        children: "To connect Sinch MessageMedia to Hightouch you need an API key and API secret from your Sinch HUB account. Hightouch authenticates each request with HTTP Basic auth."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsx(_components.a, {
              href: "https://app.hightouch.com/destinations",
              children: "Destinations overview"
            }), " page and select ", _jsx(_components.strong, {
              children: "Add destination"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Sinch MessageMedia"
            }), " from the destination catalog."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter the following credentials, then select ", _jsx(_components.strong, {
              children: "Continue"
            }), ":"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "API key"
              }), ": the API key from your Sinch HUB account."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "API secret"
              }), ": the API secret from your Sinch HUB account."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The destination targets the Sinch MessageMedia HUB Contacts API\n(", _jsx(_components.code, {
            children: "api.messagemedia.com"
          }), "). It does not currently authenticate against the Sinch\nCEP Campaigns API hosts."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "sync-contacts",
        children: "Sync contacts"
      }), "\n", _jsx(_components.p, {
        children: "Upsert contact records into your Sinch contact directory. Each row in your model becomes a contact identified by phone number."
      }), "\n", _jsx(_components.h4, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Contacts syncs run in ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode. Hightouch creates new contacts that aren't already in Sinch and updates existing contacts when fields change."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Sinch's HUB API returns a 409 conflict if a contact's phone number or alias is\nalready in use by a different record. Hightouch surfaces these conflicts as\nrejected rows in the ", _jsx(_components.a, {
            href: "#live-debugger",
            children: "Live debugger"
          }), " rather than aborting the\nsync."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Map a model column to ", _jsx(_components.strong, {
          children: "Phone number (msisdn)"
        }), ". Sinch matches contacts by phone number, in international format (", _jsx(_components.code, {
          children: "+E.164"
        }), ")."]
      }), "\n", _jsx(_components.h4, {
        id: "channel-configuration",
        children: "Channel configuration"
      }), "\n", _jsx(_components.p, {
        children: "Sinch contacts each have one or more channels. Hightouch sends the mapped phone number on every row's primary channel using the type and subscription state configured here:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Channel type"
              })
            }), _jsxs(_components.td, {
              children: ["The channel to associate with the phone number. Choose ", _jsx(_components.strong, {
                children: "SMS"
              }), " (default) or ", _jsx(_components.strong, {
                children: "WHATSAPP"
              }), ". Sinch's account-level validator rejects other values."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Default subscription state"
              })
            }), _jsxs(_components.td, {
              children: ["The subscription state applied when a row has no subscription-state column, or its value isn't recognized. Choose ", _jsx(_components.strong, {
                children: "SUBSCRIBED"
              }), " (default) or ", _jsx(_components.strong, {
                children: "UNSUBSCRIBED"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subscription state column"
              })
            }), _jsx(_components.td, {
              children: "Optional. Map a column to set each contact's subscription state from source data — for example, to drive a preference-management workflow."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For preference management, map the ", _jsx(_components.strong, {
          children: "Subscription state column"
        }), " so subscribe and unsubscribe come from your warehouse per contact instead of a single sync-wide default. The column value is matched case-insensitively and accepts ", _jsx(_components.code, {
          children: "SUBSCRIBED"
        }), "/", _jsx(_components.code, {
          children: "UNSUBSCRIBED"
        }), ", ", _jsx(_components.code, {
          children: "true"
        }), "/", _jsx(_components.code, {
          children: "false"
        }), ", ", _jsx(_components.code, {
          children: "t"
        }), "/", _jsx(_components.code, {
          children: "f"
        }), ", ", _jsx(_components.code, {
          children: "yes"
        }), "/", _jsx(_components.code, {
          children: "no"
        }), ", and ", _jsx(_components.code, {
          children: "1"
        }), "/", _jsx(_components.code, {
          children: "0"
        }), ". Any unmapped, empty, or unrecognized value falls back to the default subscription state."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Map source columns to Sinch's standard contact fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Destination field"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "First name"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Last name"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Alias"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Must be unique across your Sinch account. Duplicate aliases return 409."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Date of birth"
              })
            }), _jsx(_components.td, {
              children: "date"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Country"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "State"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Location"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Note"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To send fields that aren't in this list, configure them as ", _jsx(_components.strong, {
          children: "Custom fields"
        }), " below."]
      }), "\n", _jsx(_components.h4, {
        id: "custom-fields",
        children: "Custom fields"
      }), "\n", _jsx(_components.p, {
        children: "Map model columns to custom fields configured in your Sinch HUB account. Hightouch fetches your account's custom field definitions and shows them in the dropdown — you map a source column to a specific Sinch custom field UUID."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Create the custom fields in Sinch HUB before configuring the mapping.\nHightouch only lists custom fields that already exist in your account."
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-contact-lists",
        children: "Sync contact lists"
      }), "\n", _jsx(_components.p, {
        children: "Sync a Hightouch audience into a long-lived Sinch contact list. Best for reusable, consented populations that drive recurring campaigns."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Contact list syncs require contacts to ", _jsx(_components.strong, {
            children: "already exist in Sinch"
          }), ". Sinch's\nmembership endpoint accepts contact UUIDs only, so run the\n", _jsx(_components.a, {
            href: "#sync-contacts",
            children: "Contacts"
          }), " sync first to create the contacts you want to add.\nA phone number that Sinch has no contact for is rejected rather than created."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "choose-a-contact-list",
        children: "Choose a contact list"
      }), "\n", _jsx(_components.p, {
        children: "Select an existing Sinch contact list, or create a new one from the sync configuration form. Hightouch fetches your account's contact lists and lets you pick one."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Match each row to a Sinch contact in one of two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Phone number (msisdn)"
          }), " — Map a column of phone numbers in international format (", _jsx(_components.code, {
            children: "+E.164"
          }), "). Hightouch looks each number up on the selected channel type and uses the matched contact. This is the simplest option when your warehouse doesn't store Sinch UUIDs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contact UUID (contact_id)"
          }), " — Map a column of Sinch contact UUIDs. Use this when you already have the UUIDs, typically written back from the ", _jsx(_components.a, {
            href: "#sync-contacts",
            children: "Contacts"
          }), " sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When matching by phone number, set ", _jsx(_components.strong, {
          children: "Channel type"
        }), " to the channel the numbers belong to (", _jsx(_components.strong, {
          children: "SMS"
        }), " by default). Rows whose phone number has no matching Sinch contact are surfaced as rejected rows in the ", _jsx(_components.a, {
          href: "#live-debugger",
          children: "Live debugger"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "Choose how Hightouch treats rows that leave your audience query results:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Do nothing"
              }), " (default)"]
            }), _jsx(_components.td, {
              children: "Leave the contact's list membership unchanged."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Remove from list"
              })
            }), _jsx(_components.td, {
              children: "Remove the contact from the Sinch contact list. The contact itself is not deleted from Sinch's contact directory."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Open your Sinch HUB account and search the contact directory for one of the phone numbers you synced. Confirm that the contact appears with the expected channel type, subscription state, and mapped fields."
        }), "\n", _jsx(_components.li, {
          children: "For contact list syncs, open the target list in Sinch HUB and confirm the expected contacts are members."
        }), "\n", _jsxs(_components.li, {
          children: ["Check the ", _jsx(_components.a, {
            href: "#live-debugger",
            children: "Live debugger"
          }), " for rejected rows. The most common rejections on contact syncs are 409 conflicts caused by duplicate aliases or phone numbers already attached to a different contact."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "recommended-workflow",
        children: "Recommended workflow"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Build a Contacts sync that upserts phone numbers and attributes into Sinch. For preference management, map a subscription-state column so each contact's subscribe or unsubscribe state comes from your warehouse."
        }), "\n", _jsx(_components.li, {
          children: "Build a Contact lists sync that drives list membership from your audiences. Match on the phone number to reuse the same identifier as the Contacts sync, or on the Sinch UUID if you write those back to your warehouse."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
